/**
 * @fileoverview Prompt template for plot-thread-extractor-v1.
 *
 * Per-storyType thread-count guidance is selected at render time by the
 * `[[[plotThreadCountGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). Flash Fiction / Short Story use
 * the dedicated PLOT_THREAD_COUNT_SHORT constant (not Novel — short
 * stories really do want fewer threads). Novel is the fallback.
 */

import { PLOT_THREAD_DEFINITION, NARRATIVE_ELEMENT_DIFFERENTIATION } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story analyst specializing in narrative thread analysis. Your task is to identify and extract **plot threads** from a story — the lightweight narrative segments that track subplots, minor character arcs, mysteries, and thematic explorations woven throughout the text.

## CORE CONCEPTS

${PLOT_THREAD_DEFINITION}

### Thread Properties

Each thread has:
- **Title**: A short, evocative name (2-5 words)
- **Summary**: What this thread is about — the context that makes the question meaningful (1-3 sentences)
- **Thread Question**: The dramatic question driving reader engagement. As long as this question is unanswered, the reader is hooked.
- **Character Names**: Characters directly involved in this thread

### What to Look For

Scan the text for localized narrative sequences — specific scenarios, episodes, or tactical objectives that the characters must navigate:
1. **Action Sequences**: Heists, escapes, battles, chases, rescues (e.g., "The Shadar Logoth Escape", "The Death Star Rescue")
2. **Social Scenarios**: Tense gatherings, negotiations, confrontations (e.g., "The Netherfield Ball", "Mr. Collins' Proposal")
3. **Mysteries/Investigations**: Localized puzzles that drive specific chapters (e.g., "The Locked Room", "The Sick Triceratops")
4. **Survival Episodes**: Perilous journeys, traps, environmental challenges (e.g., "The Trash Compactor Escape", "The Tracker Jacker Attack")
5. **Ticking Clocks**: Time-pressured tactical objectives (e.g., "The Feast at the Cornucopia", "The Trench Run")
6. **Crisis Management**: Characters responding to an immediate problem (e.g., "The Elopement Crisis", "Rebooting the System")

### What NOT to Extract

${NARRATIVE_ELEMENT_DIFFERENTIATION}

Specifically:
- Don't duplicate the main A-storyline as a thread — threads are the "gears" within storylines, not the storylines themselves
- Don't extract C-Stories as threads — an independent faction's concurrent operations (e.g., "Padan Fain's Pursuit") are C-Stories, not plot threads
- Don't extract Character Arcs as threads — a character's internal psychological transformation belongs to the character arc system
- Don't extract Backstory as threads — events completed before the primary timeline begins are backstory
- Don't extract single-scene events that don't develop across multiple scenes
- Don't create threads for background exposition that doesn't generate reader questions

---

## SOURCE TEXT TO ANALYZE

{{#if textInput}}
{{{textInput}}}
{{else}}
No source text provided.
{{/if}}

---

## SECONDARY CONTEXT

<<<storyContext>>>

<<<chapterDirectory>>>

---

## EXTRACTION INSTRUCTIONS

1. **Read the full text carefully**, identifying narrative segments that have their own beginning, development, and (possibly) resolution.

2. **Extract each plot thread** with:
   - A **title** (2-5 words, evocative and specific)
   - A **summary** (1-3 sentences: what the thread is about, why it matters)
   - A **threadQuestion** (the dramatic question: phrased as a question that creates tension)
   - **characterNames** (names of characters directly involved)

3. **Be comprehensive but selective**:
[[[plotThreadCountGuidance]]]
   - Only extract genuine threads with dramatic questions

4. **Thread status**: All extracted threads should be marked as "active" unless the text clearly resolves them, in which case mark as "resolved".

5. **Chapter assignment** (when chapter data is available): For each thread, identify:
   - **introducedInChapterNumber**: The chapter number where this thread first appears. Use null if unclear.
   - **resolvedInChapterNumber**: The chapter number where this thread is conclusively resolved. Use null if the thread is still active or resolution is unclear.
   - **activeInChapterNumbers**: All chapter numbers where this thread meaningfully advances — where it receives attention, develops, or creates tension. Do not include chapters where the thread is merely referenced in passing.

   Use the chapter numbers from the CHAPTER DIRECTORY above. If no chapter data is available, set introducedInChapterNumber and resolvedInChapterNumber to null, and activeInChapterNumbers to an empty array.

Provide your output as a JSON object with a \`plotThreads\` array.
`;
